import React, { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Image,
  ImageBackground,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import {
  finishTransaction,
  getProducts,
  initConnection,
  requestPurchase,
} from "react-native-iap";

import { AppIcons } from "../../assets/images";
import { userAction } from "../../redux/reducers/user";
import { AppTheme } from "../../utils/theme";
import {
  androidSkus,
  iosSkus,
  objectIdToUUID,
  LUCKY_SPIN,
  GET_USER_DETAIL,
} from "../../utils/contants";
import api from "../../utils/axiosinstance";
import FastImage from "react-native-fast-image";
import LinearGradient from "react-native-linear-gradient";
import { useAppSelector } from "../../redux/storeHook";
import { ROUTE_NAME } from "../../utils/routes";

const InPurchaseScreen = () => {
  const navigation = useNavigation<NativeStackNavigationProp<any>>();
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState<string | null>(null); // Track loading per product ID
  const { userData } = useAppSelector((s) => s.user);

  useEffect(() => {
    initIAP();
  }, []);

  const initIAP = async () => {
    try {
      setLoading(true);
      await initConnection();
      await fetchProducts();
    } catch (error) {
      console.log("IAP init error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const result = await getProducts({
        skus: Platform.OS === "ios" ? iosSkus : androidSkus,
      });
      setProducts(result);
      console.log("Products:", result);
    } catch (error) {
      console.log("Fetch products error:", error);
    }
  };

  const completeTransaction = async (purchase: any) => {
    try {
      const res = await finishTransaction({
        purchase,
        isConsumable: true,
        developerPayloadAndroid: userData?._id,
      });
      console.log("Transaction completed:", res);

      // Calculate coins from product ID
      const rawId = String(purchase?.productId || "");
      const coinsToAdd =
        parseInt(rawId.replace(/\D/g, ""), 10) || 1000;

      try {
        await api.post(LUCKY_SPIN, { coinsToAdd });
        const profileRes = await api.get(GET_USER_DETAIL);
        if (profileRes.data?.status && profileRes.data?.user?.coins) {
          userAction.updateUserField({
            key: "coins",
            value: profileRes.data.user.coins,
          });
        }
        Alert.alert(
          "Purchase Successful! 🎉",
          `${coinsToAdd.toLocaleString()} Coins added to your balance!`
        );
      } catch (err) {
        console.log("Credit coins error:", err);
      }
    } catch (error) {
      console.error("Error completing transaction:", error);
      throw error;
    }
  };

  // const onBuyPress = async (productId: string) => {
  //   // Prevent purchase if global loading or already purchasing this item
  //   if (loading || purchaseLoading === productId) {
  //     return;
  //   }

  //   try {
  //     setPurchaseLoading(productId); // Set per-item loading
  //     // Platform-specific params for requestPurchase
  //     const purchaseParams =
  //       Platform.OS === "ios" ? { sku: productId } : { skus: [productId] };

  //     await requestPurchase(purchaseParams);
  //     // Note: After requestPurchase, you should listen for purchase updates
  //     // via addPurchaseListener or getPurchaseUpdatesAsync in a real app
  //   } catch (error) {
  //     console.log("Purchase error:", error);
  //   } finally {
  //     setPurchaseLoading(null); // Clear loading after attempt
  //   }
  // };

  const onBuyPress = async (productId: string) => {
    // Prevent purchase if global loading or already purchasing this item
    if (!userData?.access_token) {
      Alert.alert("Login", "To purchase coins, you need to log in first.", [
        {
          text: "Login",
          onPress: () => {
            navigation.navigate(ROUTE_NAME.LoginScreen);
          },
        },
        {
          text: "Cancel",
          style: "cancel",
        },
      ]);
      return;
    }
    if (loading || purchaseLoading === productId) {
      return;
    }
    try {
      setPurchaseLoading(productId); // Set per-item loading
      // Platform-specific params for requestPurchase
      const UUID = objectIdToUUID(userData?._id as any);
      console.log("@ obje ctIdToUUID", UUID);
      const purchaseParams =
        Platform.OS === "ios"
          ? { sku: productId, appAccountToken: UUID }
          : { skus: [productId],obfuscatedAccountIdAndroid:userData?._id };

      console.log("@ purchaseParams", purchaseParams);
      const purchase = await requestPurchase(purchaseParams);
      await completeTransaction(purchase);

      console.log("@ purchase requested");
      // Note: After requestPurchase, you should listen for purchase updates
      // via addPurchaseListener or getPurchaseUpdatesAsync in a real app
    } catch (error) {
      console.log("Purchase error:", error);
    } finally {
      setPurchaseLoading(null); // Clear loading after attempt
    }
  };

  return (
    <ImageBackground source={AppIcons.screenBackground} style={styles.mainView}>
      {/* 🔙 Back Button (Flush Right) */}
      <TouchableOpacity
        style={styles.backIcon}
        onPress={() => {
          navigation.goBack();
          userAction.setTouchSound(true);
        }}
        activeOpacity={0.7}
      >
        <Image
          source={AppIcons.BackIcon}
          style={styles.backImage}
          resizeMode="contain"
        />
      </TouchableOpacity>

      {/* 🔝 Top Header Bar */}
      <View style={styles.headerBar}>
        <Text style={styles.title}>COINS</Text>
      </View>

      {loading ? (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color={AppTheme.colors.white} />
          <Text style={styles.loadingText}>Loading store...</Text>
        </View>
      ) : (
        <ScrollView
          style={styles.scrollArea}
          contentContainerStyle={styles.listContainer}
          showsVerticalScrollIndicator={false}
        >
          {products.map((item) => {
            const rawNumber =
              parseInt(String(item.productId || "").replace(/\D/g, ""), 10) ||
              1000;
            const coinLabel = `${rawNumber.toLocaleString()} Coins`;

            return (
              <TouchableOpacity
                onPress={() => onBuyPress(item.productId)}
                activeOpacity={0.7}
                disabled={purchaseLoading === item.productId}
                key={item.productId}
                style={styles.itemWrapper}
              >
                <FastImage
                  source={AppIcons.CoinBig}
                  style={styles.coinImage}
                  resizeMode={FastImage.resizeMode.contain}
                >
                  <Text style={styles.buyText}>{coinLabel}</Text>
                  <Image
                    source={AppIcons.Coin_5}
                    style={{ height: 72, width: 72, marginVertical: 2 }}
                    resizeMode={"contain"}
                  />
                  <View style={{ alignSelf: "center", marginBottom: 6 }}>
                    {purchaseLoading === item.productId ? (
                      <View style={styles.purchaseLoadingContainer}>
                        <ActivityIndicator
                          size="small"
                          color={AppTheme.colors.white}
                        />
                      </View>
                    ) : (
                      <LinearGradient
                        colors={[
                          AppTheme.colors.LinearGradient_13,
                          AppTheme.colors.LinearGradient_13,
                        ]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={styles.googleBtn1}
                      >
                        <LinearGradient
                          colors={[
                            AppTheme.colors.linearGradient_11,
                            AppTheme.colors.linearGradient_12,
                          ]}
                          start={{ x: 0, y: 0 }}
                          end={{ x: 1, y: 1 }}
                          style={styles.googleBtn}
                        >
                          <Text style={styles.startText}>
                            {item.localizedPrice}
                          </Text>
                        </LinearGradient>
                      </LinearGradient>
                    )}
                  </View>
                </FastImage>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}
    </ImageBackground>
  );
};

export default InPurchaseScreen;

const styles = StyleSheet.create({
  mainView: {
    flex: 1,
    width: "100%",
  },

  headerBar: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: Platform.OS === "web" ? 22 : 52,
    paddingBottom: 15,
  },

  backIcon: {
    position: "absolute",
    top: Platform.OS === "web" ? 20 : 50,
    right: 0,
    zIndex: 999,
    elevation: 999,
  },

  title: {
    fontSize: 32,
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    color: AppTheme.colors.white,
    textShadowColor: AppTheme.colors.outline,
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 3,
    letterSpacing: 1,
    textAlign: "center",
  },

  backImage: {
    width: 48,
    height: 38,
  },

  loadingOverlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },

  loadingText: {
    marginTop: 10,
    fontSize: 18,
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    color: AppTheme.colors.white,
  },

  scrollArea: {
    width: "100%",
    flex: 1,
  },

  listContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-around",
    paddingHorizontal: 8,
    paddingTop: 8,
    paddingBottom: 70,
  },

  itemWrapper: {
    width: "47%",
    alignItems: "center",
    marginVertical: 10,
  },

  coinImage: {
    width: "100%",
    height: 200,
    paddingTop: 14,
    paddingBottom: 20,
    paddingHorizontal: 8,
    alignItems: "center",
    justifyContent: "space-between",
  },

  buyText: {
    fontSize: 19,
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    color: AppTheme.colors.white,
    textShadowColor: AppTheme.colors.outline,
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 2,
    textAlign: "center",
  },

  googleBtn1: {
    width: 108,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
  },

  googleBtn: {
    width: 102,
    height: 30,
    borderRadius: 15,
    justifyContent: "center",
    alignItems: "center",
  },

  startText: {
    fontSize: 17,
    fontFamily: AppTheme.fonts.BUBBLE_BOBBLE,
    color: AppTheme.colors.white,
    textAlign: "center",
    textShadowColor: AppTheme.colors.outline,
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
  },
  purchaseLoadingContainer: {
    width: Platform.OS == "ios" ? 90 : 100,
    height: 35,
    borderRadius: 360,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.2)", // Semi-transparent background for spinner
  },
  loadingOverlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});
